/**
 * Sessions Page - Active Collaboration Session Monitoring
 *
 * Displays real-time view of active collaborative editing sessions,
 * showing who is editing which cards and session duration.
 *
 * Uses polling (30s interval) to refresh session data.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useStatus } from '../contexts/StatusProvider';
import { Make_Authorized_API_Call, isSuccessResponse } from '../api/apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';
import { FiUsers, FiRefreshCw, FiClock, FiFileText, FiActivity } from 'react-icons/fi';

interface SessionUser {
  connection_id: string;
  user_id: string;
  user_name: string;
  connected_at: string;
  last_activity_at: string;
}

interface CollaborationSession {
  card_id: string;
  card_title: string | null;
  card_type: string | null;
  card_state: string | null;
  active_connections: number;
  users: SessionUser[];
  session_started_at: string;
  last_activity: string;
}

const SessionsPage: React.FC = () => {
  const { Client_ID } = useStatus();
  const [sessions, setSessions] = useState<CollaborationSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchSessions = useCallback(async () => {
    if (!Client_ID) return;

    try {
      const response = await Make_Authorized_API_Call<CollaborationSession>(
        API_ENDPOINTS.COLLABORATION.SESSIONS,
        'GET',
        undefined,
        { params: { Client_ID } }
      );

      if (isSuccessResponse(response)) {
        setSessions(response.data?.Records || []);
        setError('');
      } else {
        setError(response.message || 'Failed to load sessions');
      }
    } catch (err) {
      console.error('[SessionsPage] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, [Client_ID]);

  // Initial load + polling every 30 seconds
  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 30000);
    return () => clearInterval(interval);
  }, [fetchSessions]);

  const formatDuration = (startDate: string): string => {
    const diff = Date.now() - new Date(startDate).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m`;
    return 'Just started';
  };

  const formatTimeAgo = (date: string): string => {
    const diff = Date.now() - new Date(date).getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);

    if (minutes > 0) return `${minutes}m ago`;
    return `${seconds}s ago`;
  };

  const totalUsers = sessions.reduce((sum, s) => sum + s.active_connections, 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiActivity className="w-6 h-6 text-indigo-600" />
            Active Sessions
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Real-time collaborative editing sessions
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            onClick={fetchSessions}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-700 disabled:opacity-50"
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <FiFileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{sessions.length}</p>
              <p className="text-sm text-gray-500">Active Sessions</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <FiUsers className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{totalUsers}</p>
              <p className="text-sm text-gray-500">Connected Users</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <FiClock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {sessions.length > 0 ? formatDuration(sessions[0]?.session_started_at) : '—'}
              </p>
              <p className="text-sm text-gray-500">Longest Session</p>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Sessions List */}
      {loading && sessions.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FiUsers className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Sessions</h3>
          <p className="text-gray-500">
            When users open cards for collaborative editing, their sessions will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {sessions.map((session) => (
            <div
              key={session.card_id}
              className="bg-white rounded-lg border border-gray-200 p-5 hover:border-indigo-300 transition-colors"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {session.card_type && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-800">
                        {session.card_type}
                      </span>
                    )}
                    {session.card_state && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600">
                        {session.card_state}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {session.card_title || `Card ${session.card_id.substring(0, 8)}...`}
                  </h3>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <FiClock className="w-4 h-4" />
                  <span>{formatDuration(session.session_started_at)}</span>
                </div>
              </div>

              {/* Users in session */}
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {session.users.slice(0, 5).map((user) => (
                    <div
                      key={user.connection_id}
                      className="w-8 h-8 rounded-full bg-indigo-600 border-2 border-white flex items-center justify-center text-white text-xs font-bold"
                      title={`${user.user_name} — connected ${formatTimeAgo(user.connected_at)}`}
                    >
                      {user.user_name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  ))}
                  {session.users.length > 5 && (
                    <div className="w-8 h-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-gray-600 text-xs font-bold">
                      +{session.users.length - 5}
                    </div>
                  )}
                </div>
                <span className="text-sm text-gray-600">
                  {session.active_connections} user{Number(session.active_connections) !== 1 ? 's' : ''} editing
                </span>
                <span className="text-xs text-gray-400">
                  Last activity: {formatTimeAgo(session.last_activity)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SessionsPage;
